import { z } from 'zod';

// ==================== VALIDATION PATTERNS ====================

/**
 * Format année universitaire: AAAA/AAAA+1 (ex: 2020/2021)
 * Vérifie que la deuxième année = première année + 1
 */
const formatAnneeUniversitaire = /^\d{4}\/\d{4}$/;

const validateAnneeUniversitaire = (value: string): boolean => {
  if (!formatAnneeUniversitaire.test(value)) return false;
  const [annee1, annee2] = value.split('/').map(Number);
  return annee2 === annee1 + 1;
};

// ==================== ZOD SCHEMAS ====================

/**
 * Schéma de validation des champs spécifiques étudiant (E-DEP)
 * Tous les champs sont obligatoires pour la conformité ministérielle
 */
export const champsEtudiantSchema = z.object({
  // ===== Inscription Académique =====

  numeroMassarCNE: z
    .string()
    .min(1, 'N° Massar/CNE obligatoire')
    .regex(
      /^[A-Za-z0-9]+$/,
      'N° Massar/CNE invalide (lettres et chiffres uniquement)'
    ),

  diplomeInscrit: z
    .string()
    .min(1, 'Diplôme d\'inscription obligatoire'),

  filiere: z
    .string()
    .min(1, 'Filière obligatoire'),

  codeFiliere: z
    .string()
    .min(1, 'Code filière obligatoire'),

  niveauFormation: z
    .number({
      required_error: 'Niveau de formation obligatoire',
      invalid_type_error: 'Niveau de formation doit être un nombre',
    })
    .int('Niveau de formation doit être un entier')
    .min(1, 'Niveau minimum: 1')
    .max(10, 'Niveau maximum: 10'),

  situation: z.enum(['N', 'R', 'P', 'D'], {
    required_error: 'Situation obligatoire',
    invalid_type_error: 'Situation invalide',
  }).describe('N: Nouveau inscrit, R: Redoublant, P: Promu, D: Diplômé'),

  // ===== Diplôme d'Accès =====

  diplomeAcces: z
    .string()
    .min(1, 'Diplôme d\'accès obligatoire'),

  serieBaccalaureat: z
    .string()
    .min(1, 'Série du baccalauréat obligatoire'),

  anneeObtentionBac: z
    .string()
    .min(1, 'Année d\'obtention du baccalauréat obligatoire')
    .refine(validateAnneeUniversitaire, {
      message: 'Format invalide. Utilisez AAAA/AAAA+1 (ex: 2020/2021)',
    }),

  specialiteDiplomeAcces: z
    .string()
    .min(1, 'Spécialité du diplôme d\'accès obligatoire'),

  mentionDiplomeAcces: z.enum(['p', 'ab', 'b', 'tb', 'e', 'ns'], {
    required_error: 'Mention du diplôme obligatoire',
    invalid_type_error: 'Mention invalide',
  }).describe('p: Passable, ab: Assez bien, b: Bien, tb: Très bien, e: Excellent, ns: Non spécifié'),

  lieuObtentionDiplomeAcces: z
    .string()
    .min(1, 'Lieu d\'obtention du diplôme obligatoire'),

  // ===== Statut et Inscription =====

  premiereAnneeInscription: z
    .string()
    .min(1, '1ère année d\'inscription obligatoire')
    .refine(validateAnneeUniversitaire, {
      message: 'Format invalide. Utilisez AAAA/AAAA+1 (ex: 2021/2022)',
    }),

  resident: z.enum(['O', 'N'], {
    required_error: 'Statut résident obligatoire',
    invalid_type_error: 'Statut résident invalide',
  }).describe('O: Oui (hébergé en résidence), N: Non'),

  fonctionnaire: z.enum(['O', 'N'], {
    required_error: 'Statut fonctionnaire obligatoire',
    invalid_type_error: 'Statut fonctionnaire invalide',
  }).describe('O: Oui (fonctionnaire), N: Non'),

  pourcentageBourse: z
    .number({
      required_error: 'Pourcentage de bourse obligatoire',
      invalid_type_error: 'Pourcentage de bourse doit être un nombre',
    })
    .min(0, 'Pourcentage minimum: 0%')
    .max(100, 'Pourcentage maximum: 100%'),

  mobilite: z.enum(['O', 'N'], {
    required_error: 'Statut mobilité obligatoire',
    invalid_type_error: 'Statut mobilité invalide',
  }).describe('O: Oui (en mobilité), N: Non'),
});

/**
 * Schéma complet du formulaire étudiant avec validation croisée
 */
export const etudiantFormSchema = champsEtudiantSchema.superRefine((data, ctx) => {
  // Validation: année obtention bac doit être <= première année inscription
  if (data.anneeObtentionBac && data.premiereAnneeInscription) {
    const anneeBac = parseInt(data.anneeObtentionBac.split('/')[0], 10);
    const anneeInscription = parseInt(data.premiereAnneeInscription.split('/')[0], 10);

    if (anneeBac > anneeInscription) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'L\'année d\'obtention du bac ne peut pas être postérieure à la 1ère année d\'inscription',
        path: ['anneeObtentionBac'],
      });
    }
  }

  // Validation: niveau de formation cohérent avec situation
  if (data.situation === 'N' && data.niveauFormation > 5) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Un nouveau inscrit ne peut généralement pas être au-delà du niveau 5',
      path: ['niveauFormation'],
    });
  }
});

// ==================== TYPES INFÉRÉS ====================

export type ChampsEtudiantSchemaType = z.infer<typeof champsEtudiantSchema>;
export type EtudiantFormSchemaType = z.infer<typeof etudiantFormSchema>;

// ==================== HELPERS ====================

/**
 * Génère l'année universitaire actuelle au format AAAA/AAAA+1
 */
export const getAnneeUniversitaireActuelle = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  // L'année universitaire commence en septembre
  const startYear = now.getMonth() >= 8 ? year : year - 1;
  return `${startYear}/${startYear + 1}`;
};

/**
 * Génère une liste d'années universitaires (pour les dropdowns)
 * @param count Nombre d'années à générer (par défaut 20)
 * @param startFromCurrent Commencer de l'année actuelle (par défaut true)
 */
export const generateAnneesUniversitaires = (
  count: number = 20,
  startFromCurrent: boolean = true
): string[] => {
  const now = new Date();
  const currentYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  const startYear = startFromCurrent ? currentYear : currentYear - count + 1;

  return Array.from({ length: count }, (_, i) => {
    const year = startYear - i;
    return `${year}/${year + 1}`;
  });
};

/**
 * Valide un numéro Massar/CNE
 * - Marocains: code alphanumérique (ex: R123456789)
 * - Étrangers: 'be' (boursier étranger)
 */
export const validateMassarCNE = (value: string): boolean => {
  if (value.toLowerCase() === 'be') return true;
  return /^[A-Za-z]\d{9,}$/.test(value);
};
